<template>
  <!-- ------ 基准面属性面板样式统一 开始------ -->
  <div class="plane-property sw-plane-property" data-testid="assemble-datum-plane-property-section">
    <n-collapse :default-expanded-names="defaultExpandedNames" class="pm-collapse">
      <n-collapse-item title="信息" name="info">
        <div class="status-banner" :class="statusClass">{{ statusText }}</div>
      </n-collapse-item>

      <n-collapse-item
        v-for="slotPanel in slotPanels"
        :key="slotPanel.slot"
        :title="slotPanel.title"
        :name="slotPanel.slot"
      >
        <button
          type="button"
          class="reference-input"
          :class="{ active: slotPanel.active }"
          @click="setActiveSlot(slotPanel.slot)"
          @contextmenu.prevent="(event) => openReferenceContextMenu(event, slotPanel.entityId)"
        >
          <span class="slot-accent" :style="{ backgroundColor: slotPanel.color }" />
          <span class="reference-text">{{ slotPanel.label || '选择参考' }}</span>
        </button>

        <!-- ------ 基准面两侧对称槽位标识 开始------ -->
        <div v-if="showSlotMidPlaneTag(slotPanel.slot)" class="slot-midplane-tag">
          两侧对称
        </div>
        <!-- ------ 基准面两侧对称槽位标识 结束------ -->

        <div
          v-if="shouldRenderReferenceDetail(slotPanel.slot)"
          class="reference-detail"
        >
          <n-radio-group
            :value="resolveConstraintValueBySlot(slotPanel.slot)"
            @update:value="(value) => handleConstraintRadioChange(slotPanel.slot, value)"
          >
            <n-space vertical size="small">
              <n-radio
                v-for="constraint in resolveConstraintButtonsBySlot(slotPanel.slot)"
                :key="constraint"
                :value="constraint"
                :disabled="!isConstraintEnabledForSlot(slotPanel.slot, constraint)"
              >
                {{ resolveConstraintLabel(constraint) }}
              </n-radio>
            </n-space>
          </n-radio-group>

          <template v-if="shouldRenderPlaneAdvancedPanel(slotPanel.slot)">
            <div v-if="currentConstraint === PlaneConstraintType.Angle" class="scalar-row">
              <span class="scalar-name">角度</span>
              <n-input-number
                :value="angleValue"
                :step="1"
                :precision="2"
                :show-button="true"
                :disabled="!angleFieldEditable"
                @update:value="(value) => setDistanceOrAngle(Number(value ?? 0))"
              />
              <span class="unit-text">度</span>
            </div>

            <div v-if="currentConstraint === PlaneConstraintType.Distance" class="scalar-row">
              <span class="scalar-name">偏移距离</span>
              <n-input-number
                :value="distanceValue"
                :step="1"
                :precision="8"
                :show-button="true"
                :disabled="!distanceFieldEditable"
                @update:value="(value) => setDistanceOrAngle(Number(value ?? 0))"
              />
              <span class="unit-text">mm</span>
            </div>

            <n-checkbox
              v-if="showReverseOffsetOption"
              class="reverse-offset-check"
              :checked="reverseOffset"
              :disabled="!reverseOffsetEditable"
              @update:checked="(value) => setReverseOffset(Boolean(value))"
            >
              反转等距
            </n-checkbox>

            <div v-if="showPlaneCountField" class="count-row">
              <span class="count-name">基准面数</span>
              <n-input-number
                :value="planeCount"
                :min="1"
                :step="1"
                :show-button="true"
                :disabled="!planeCountEditable"
                @update:value="(value) => setPlaneCount(Math.max(1, Math.floor(Number(value ?? 1))))"
              />
            </div>

            <div v-if="showSolutionIndexField" class="count-row solution-row">
              <span class="count-name">候选解</span>
              <n-input-number
                :value="solutionIndex"
                :min="0"
                :max="Math.max(0, solutionCount - 1)"
                :step="1"
                :show-button="true"
                @update:value="(value) => setSolutionIndex(Math.max(0, Math.floor(Number(value ?? 0))))"
              />
            </div>
          </template>
        </div>
      </n-collapse-item>

      <n-collapse-item title="选项" name="options">
        <n-space vertical size="small">
          <n-checkbox
            v-if="showReverseNormalOption"
            :checked="reverseNormal"
            @update:checked="(value) => setReverseNormal(Boolean(value))"
          >
            反转法线
          </n-checkbox>
          <n-checkbox
            v-if="showOriginOnCurveOption"
            :checked="originOnCurve"
            @update:checked="(value) => setOriginOnCurve(Boolean(value))"
          >
            原点设在曲线上
          </n-checkbox>
        </n-space>

        <div v-if="showProjectionTypeSection" class="projection-block">
          <div class="projection-title">曲面投影类型</div>
          <n-radio-group :value="projectionType" @update:value="setProjectionType">
            <n-space vertical size="small">
              <n-radio :value="PlaneProjectionType.NearestOnSurface">最近位置</n-radio>
              <n-radio :value="PlaneProjectionType.AlongSketchNormal">沿草图法线</n-radio>
            </n-space>
          </n-radio-group>
        </div>
      </n-collapse-item>
    </n-collapse>

    <n-dropdown
      placement="bottom-start"
      trigger="manual"
      :x="contextMenuX"
      :y="contextMenuY"
      :show="showContextMenu"
      :options="contextMenuOptions"
      @select="handleContextMenuSelect"
      @clickoutside="showContextMenu = false"
    />
  </div>
  <!-- ------ 基准面属性面板样式统一 结束------ -->
</template>

<script setup lang="ts">

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NDropdown,
  NInputNumber,
  NRadio,
  NRadioGroup,
  NSpace,
  type DropdownOption,
} from 'naive-ui';
import {
  convertMToMm,
  convertMmToM,
  PlaneConstraintType,
  PlaneProjectionType,
  PlaneSlotKey,
  PlaneSolveStatus,
  ReferenceEntitySemanticType,
  roundNullableToEightDecimalPlaces,
} from '@puppycad/cad-core';
import { useViewer } from '@/hooks';

/**
 * @description 基准面属性面板内部使用的选择分类枚举。
 */
enum PlaneSelectionKind {
  /** 点类实体。 */
  PointLike = 'pointLike',
  /** 线类实体。 */
  Linear = 'linear',
  /** 平面类实体。 */
  PlaneLike = 'planeLike',
  /** 曲面类实体。 */
  SurfaceLike = 'surfaceLike',
  /** 不支持实体。 */
  Unsupported = 'unsupported',
}

/**
 * @description 将统一语义实体类型归一化为属性面板使用的选择分类。
 *
 * @param entityType 统一语义实体类型。
 * @returns 属性面板使用的选择分类。
 */
function normalizePlaneSelectionKind(
  entityType: ReferenceEntitySemanticType | PlaneSelectionKind | null | undefined,
): PlaneSelectionKind {
  if (entityType === PlaneSelectionKind.PointLike
    || entityType === PlaneSelectionKind.Linear
    || entityType === PlaneSelectionKind.PlaneLike
    || entityType === PlaneSelectionKind.SurfaceLike
    || entityType === PlaneSelectionKind.Unsupported) {
    return entityType;
  }

  switch (entityType) {
    case ReferenceEntitySemanticType.FeaturePoint:
    case ReferenceEntitySemanticType.DatumPoint:
      return PlaneSelectionKind.PointLike;
    case ReferenceEntitySemanticType.FeatureLine:
    case ReferenceEntitySemanticType.FeatureCurve:
    case ReferenceEntitySemanticType.DatumAxis:
      return PlaneSelectionKind.Linear;
    case ReferenceEntitySemanticType.FeatureFace:
    case ReferenceEntitySemanticType.DatumPlane:
      return PlaneSelectionKind.PlaneLike;
    case ReferenceEntitySemanticType.FeatureSurface:
      return PlaneSelectionKind.SurfaceLike;
    default:
      return PlaneSelectionKind.Unsupported;
  }
}

/**
 * @description 参考槽位类型定义。
 */
type PlaneSlot = 'reference-1' | 'reference-2' | 'reference-3';


/**
 * @description PropertyManager 中单个参考槽位的已选数据。
 */
interface SlotSelectionEntry {
  /**
   * 参考槽位键值。
   */
  slot: PlaneSlot;
  /**
   * 参考标签文本。
   */
  label: string;
  /**
   * 参考实体 ID。
   */
  entityId: string;
  /**
   * 参考类别。
   */
  kind: PlaneSelectionKind;
}

/**
 * @description 槽位面板渲染项。
 */
interface SlotPanel {
  /**
   * 槽位键值。
   */
  slot: PlaneSlot;
  /**
   * 面板标题。
   */
  title: string;
  /**
   * 槽位色条颜色。
   */
  color: string;
  /**
   * 是否高亮为当前活动槽位。
   */
  active: boolean;
  /**
   * 绑定实体 ID。
   */
  entityId: string | null;
  /**
   * 显示标签。
   */
  label: string;
}


defineOptions({
  inheritAttrs: false,
});

const { getViewer } = useViewer();


interface ReferencePlanePanelSnapshot {
  datumPlaneActiveReferenceKey: PlaneSlot | null;
  referencePlaneSelections: SlotSelectionEntry[];
  referencePlaneSolveStatus: PlaneSolveStatus;
  referencePlaneConstraint: PlaneConstraintType;
  referencePlaneAvailableConstraints: PlaneConstraintType[];
  referencePlaneDistanceOrAngle: number;
  referencePlaneReverseNormal: boolean;
  referencePlaneReverseOffset: boolean;
  referencePlaneOriginOnCurve: boolean;
  referencePlanePlaneCount: number;
  referencePlaneSolutionIndex: number;
  referencePlaneSolutionCount: number;
  referencePlaneProjectionType: PlaneProjectionType;
  referencePlaneErrorMessage: string;
}

const DEFAULT_REFERENCE_PLANE_SNAPSHOT: ReferencePlanePanelSnapshot = {
  datumPlaneActiveReferenceKey: 'reference-1',
  referencePlaneSelections: [],
  referencePlaneSolveStatus: PlaneSolveStatus.UnderDefined,
  referencePlaneConstraint: PlaneConstraintType.Auto,
  referencePlaneAvailableConstraints: [PlaneConstraintType.Auto],
  referencePlaneDistanceOrAngle: 0,
  referencePlaneReverseNormal: false,
  referencePlaneReverseOffset: false,
  referencePlaneOriginOnCurve: false,
  referencePlanePlaneCount: 1,
  referencePlaneSolutionIndex: 0,
  referencePlaneSolutionCount: 1,
  referencePlaneProjectionType: PlaneProjectionType.NearestOnSurface,
  referencePlaneErrorMessage: '',
};

const localSnapshot = ref<ReferencePlanePanelSnapshot>({ ...DEFAULT_REFERENCE_PLANE_SNAPSHOT });

function syncReferencePlaneSnapshot(): void {
  const controller = getViewer()?.referencePlaneController;
  if (!controller) {
    localSnapshot.value = { ...DEFAULT_REFERENCE_PLANE_SNAPSHOT, referencePlaneSelections: [] };
    return;
  }

  const viewModel = controller.getViewModel();
  const slotOrder: PlaneSlot[] = ['reference-1', 'reference-2', 'reference-3'];
  const selections: SlotSelectionEntry[] = slotOrder
    .map((slot) => {
      const candidate = viewModel.state.refs[slot];
      if (!candidate) {
        return null;
      }
      return {
        slot,
        label: candidate.label,
        entityId: candidate.entityId,
        kind: normalizePlaneSelectionKind(candidate.kind),
      };
    })
    .filter((item): item is SlotSelectionEntry => item !== null);

  localSnapshot.value = {
    datumPlaneActiveReferenceKey: (viewModel.state.activeSlot as PlaneSlot) ?? 'reference-1',
    referencePlaneSelections: selections,
    referencePlaneSolveStatus: viewModel.state.solveStatus,
    referencePlaneConstraint: viewModel.state.constraint,
    referencePlaneAvailableConstraints: controller.getAvailableConstraints(),
    referencePlaneDistanceOrAngle: viewModel.state.distanceOrAngle,
    referencePlaneReverseNormal: viewModel.state.reverseNormal,
    referencePlaneReverseOffset: viewModel.state.reverseOffset,
    referencePlaneOriginOnCurve: viewModel.state.originOnCurve,
    referencePlanePlaneCount: viewModel.state.planeCount,
    referencePlaneSolutionIndex: viewModel.state.solutionIndex,
    referencePlaneSolutionCount: viewModel.state.preview.result?.solutionCount ?? 1,
    referencePlaneProjectionType: viewModel.state.projectionType,
    referencePlaneErrorMessage: viewModel.state.preview.error ?? '',
  };
}

let referencePlaneSyncTimer: number | null = null;

onMounted(() => {
  syncReferencePlaneSnapshot();
  
  /**
   * 通过轻量轮询同步控制器状态，保证“视口选择 -> 面板刷新”链路实时可见。
   */
  referencePlaneSyncTimer = window.setInterval(() => {
    syncReferencePlaneSnapshot();
  }, 120);
  
});

onBeforeUnmount(() => {
  if (referencePlaneSyncTimer !== null) {
    clearInterval(referencePlaneSyncTimer);
    referencePlaneSyncTimer = null;
  }
});

/**
 * @description 默认展开的分组。
 */
const defaultExpandedNames = ['info', 'reference-1', 'options'];

/**
 * @description 右键菜单显示状态。
 */
const showContextMenu = ref(false);

/**
 * @description 右键菜单 X 坐标。
 */
const contextMenuX = ref(0);

/**
 * @description 右键菜单 Y 坐标。
 */
const contextMenuY = ref(0);

/**
 * @description 右键菜单命中的实体 ID。
 */
const contextMenuEntityId = ref<string | null>(null);

/**
 * @description 右键菜单项。
 */
const contextMenuOptions = computed<DropdownOption[]>(() => [
  { label: '删除参考', key: 'delete-reference' },
]);

/**
 * @description 当前激活槽位。
 */
const activeSlot = computed<PlaneSlot>(() => (
  (localSnapshot.value.datumPlaneActiveReferenceKey as PlaneSlot | null) ?? 'reference-1'
));

/**
 * @description 当前已选参考映射。
 */
const selectedBySlot = computed(() => {
  const mapping = new Map<PlaneSlot, SlotSelectionEntry>();
  (localSnapshot.value.referencePlaneSelections ?? []).forEach((item) => {
    // 将 PropertyManager 上下文中的参考项标准化到槽位映射，便于后续规则判断。
    mapping.set(item.slot as PlaneSlot, {
      slot: item.slot as PlaneSlot,
      label: item.label,
      entityId: item.entityId,
      kind: normalizePlaneSelectionKind(item.kind),
    });
  });
  return mapping;
});

/**
 * @description 视觉激活槽位。
 * 规则：
 * 1. 当内核已自动推进到第二参考，但第二/第三参考尚未选择时，仍高亮第一参考；
 * 2. 其余场景使用运行态激活槽位。
 */
const visualActiveSlot = computed<PlaneSlot>(() => {
  const runtime = activeSlot.value;
  const hasFirst = selectedBySlot.value.has('reference-1');
  const hasSecond = selectedBySlot.value.has('reference-2');
  const hasThird = selectedBySlot.value.has('reference-3');
  if (runtime === 'reference-2' && hasFirst && !hasSecond && !hasThird) {
    return 'reference-1';
  }
  return runtime;
});

/**
 * @description 槽位面板渲染数据。
 */
const slotPanels = computed<SlotPanel[]>(() => {
  const slotDefs: Array<{ slot: PlaneSlot; title: string; color: string }> = [
    { slot: 'reference-1', title: '第一参考', color: '#36a3ff' },
    { slot: 'reference-2', title: '第二参考', color: '#ff7dc9' },
    { slot: 'reference-3', title: '第三参考', color: '#8d2dff' },
  ];
  return slotDefs.map((slotDef) => {
    const selected = selectedBySlot.value.get(slotDef.slot);
    return {
      ...slotDef,
      active: visualActiveSlot.value === slotDef.slot,
      entityId: selected?.entityId ?? null,
      label: selected?.label ?? '',
    };
  });
});


/**
 * @description 槽位约束面板类型枚举。
 */
enum PlaneSlotConstraintPanelType {
  /**
   * 平面/曲面参考面板。
   */
  Plane = 'plane',
  /**
   * 线参考面板。
   */
  Line = 'line',
  /**
   * 点参考面板。
   */
  Point = 'point',
}

/**
 * @description 解析槽位当前选择类型。
 * @param slot 槽位键值。
 * @returns 槽位选择类型，不存在时返回 `null`。
 */
function resolveSlotSelectionKind(slot: PlaneSlot): PlaneSelectionKind | null {
  return selectedBySlot.value.get(slot)?.kind ?? null;
}

/**
 * @description 判断指定槽位是否需要渲染详细约束区。
 * 规则：
 * 1. 仅当槽位已选择且类别受支持时显示；
 * 2. 只要槽位中存在面/曲面参考，就必须显示对应面的属性面板。
 * @param slot 当前槽位。
 * @returns 是否显示详细约束区。
 */
function shouldRenderReferenceDetail(slot: PlaneSlot): boolean {
  const kind = resolveSlotSelectionKind(slot);
  return kind !== null && kind !== PlaneSelectionKind.Unsupported;
}

/**
 * @description 判断当前是否处于“单曲面待相切”状态。
 * @returns 若第一参考为曲面且尚未补充其它参考，返回 `true`。
 */
function isSingleSurfaceTangentPending(): boolean {
  const firstKind = resolveSlotSelectionKind('reference-1');
  const secondKind = resolveSlotSelectionKind('reference-2');
  const thirdKind = resolveSlotSelectionKind('reference-3');
  return (
    currentConstraint.value === PlaneConstraintType.Tangent
    && firstKind === PlaneSelectionKind.SurfaceLike
    && secondKind === null
    && thirdKind === null
  );
}

/**
 * @description 解析槽位约束面板类型。
 * @param slot 槽位键值。
 * @returns 槽位约束面板类型。
 */
function resolveSlotConstraintPanelType(slot: PlaneSlot): PlaneSlotConstraintPanelType {
  const kind = resolveSlotSelectionKind(slot);
  if (kind === PlaneSelectionKind.PointLike) {
    return PlaneSlotConstraintPanelType.Point;
  }
  if (kind === PlaneSelectionKind.Linear) {
    return PlaneSlotConstraintPanelType.Line;
  }
  return PlaneSlotConstraintPanelType.Plane;
}


/**
 * @description 平面参考约束按钮集合（通过 n-radio 控制）。
 */
const planeReferenceConstraintButtons: ReadonlyArray<PlaneConstraintType> = [
  PlaneConstraintType.Parallel,
  PlaneConstraintType.Perpendicular,
  PlaneConstraintType.Coincident,
  PlaneConstraintType.Angle,
  PlaneConstraintType.Distance,
  PlaneConstraintType.MidPlane,
];


/**
 * @description 线参考专用约束按钮集合（按截图顺序）。
 */
const lineReferenceConstraintButtons: ReadonlyArray<PlaneConstraintType> = [
  PlaneConstraintType.Perpendicular,
  PlaneConstraintType.Coincident,
  PlaneConstraintType.Project,
];

/**
 * @description 点参考专用约束按钮集合。
 */
const pointReferenceConstraintButtons: ReadonlyArray<PlaneConstraintType> = [
  PlaneConstraintType.Coincident,
  PlaneConstraintType.Project,
];



/**
 * @description 解析指定槽位的约束按钮集合。
 * @param slot 槽位键值。
 * @returns 槽位应展示的约束按钮列表。
 */
function resolveConstraintButtonsBySlot(slot: PlaneSlot): ReadonlyArray<PlaneConstraintType> {
  const surfacePlaneMixedConstraint = resolveSurfacePlaneMixedSlotConstraint(slot);
  if (surfacePlaneMixedConstraint === PlaneConstraintType.Tangent) {
    return [PlaneConstraintType.Tangent];
  }
  const surfacePointMixedConstraint = resolveSurfacePointMixedSlotConstraint(slot);
  if (surfacePointMixedConstraint === PlaneConstraintType.Tangent) {
    return [PlaneConstraintType.Tangent];
  }
  if (isSurfacePointMixedPointSlot(slot)) {
    return pointReferenceConstraintButtons;
  }
  if (isSurfacePlaneMixedPlaneSlot(slot)) {
    return [PlaneConstraintType.Perpendicular, PlaneConstraintType.Parallel];
  }
  if (isSingleSurfaceTangentPending() && resolveSlotSelectionKind(slot) === PlaneSelectionKind.SurfaceLike) {
    return [PlaneConstraintType.Tangent];
  }
  const panelType = resolveSlotConstraintPanelType(slot);
  if (panelType === PlaneSlotConstraintPanelType.Point) {
    return pointReferenceConstraintButtons;
  }
  if (panelType === PlaneSlotConstraintPanelType.Line) {
    return lineReferenceConstraintButtons;
  }
  return planeReferenceConstraintButtons;
}

/**
 * @description 判断指定槽位是否应显示平面高级参数区。
 * @param slot 槽位键值。
 * @returns 仅平面/曲面参考槽位返回 `true`。
 */
function shouldRenderPlaneAdvancedPanel(slot: PlaneSlot): boolean {
  if (isSingleSurfaceTangentPending()) {
    return false;
  }
  if (isSurfacePointMixedPair()) {
    return isSurfacePointMixedSurfaceSlot(slot);
  }
  if (isSurfacePlaneMixedPair()) {
    return isSurfacePlaneMixedSurfaceSlot(slot);
  }
  return resolveSlotConstraintPanelType(slot) === PlaneSlotConstraintPanelType.Plane;
}


/**
 * @description 当前求解状态。
 */
const solveStatus = computed(() => localSnapshot.value.referencePlaneSolveStatus ?? PlaneSolveStatus.UnderDefined);

/**
 * @description 状态显示文案。
 */
const statusText = computed(() => {
  if (solveStatus.value === PlaneSolveStatus.FullyDefined) {
    return '完全定义';
  }
  if (solveStatus.value === PlaneSolveStatus.Inconsistent || solveStatus.value === PlaneSolveStatus.NoSolution) {
    return localSnapshot.value.referencePlaneErrorMessage || '无解/冲突';
  }
  return '未完全定义';
});

/**
 * @description 状态样式类。
 */
const statusClass = computed(() => {
  if (solveStatus.value === PlaneSolveStatus.FullyDefined) {
    return 'status-success';
  }
  if (solveStatus.value === PlaneSolveStatus.Inconsistent || solveStatus.value === PlaneSolveStatus.NoSolution) {
    return 'status-error';
  }
  return 'status-warning';
});

/**
 * @description 当前约束。
 */
const currentConstraint = computed(() => localSnapshot.value.referencePlaneConstraint ?? PlaneConstraintType.Auto);


/**
 * @description 判断当前是否处于“曲面 + 平面”混合模式。
 * @returns 仅两参考且一曲面一平面时返回 `true`。
 */
function isSurfacePlaneMixedPair(): boolean {
  const firstKind = resolveSlotSelectionKind('reference-1');
  const secondKind = resolveSlotSelectionKind('reference-2');
  const thirdKind = resolveSlotSelectionKind('reference-3');
  if (thirdKind !== null) {
    return false;
  }
  const isSurfacePlane = firstKind === PlaneSelectionKind.SurfaceLike && secondKind === PlaneSelectionKind.PlaneLike;
  const isPlaneSurface = firstKind === PlaneSelectionKind.PlaneLike && secondKind === PlaneSelectionKind.SurfaceLike;
  return isSurfacePlane || isPlaneSurface;
}

/**
 * @description 判断指定槽位在“曲面 + 平面”混合模式下是否为曲面槽位。
 * @param slot 槽位键值。
 * @returns 命中曲面槽位时返回 `true`。
 */
function isSurfacePlaneMixedSurfaceSlot(slot: PlaneSlot): boolean {
  return isSurfacePlaneMixedPair() && resolveSlotSelectionKind(slot) === PlaneSelectionKind.SurfaceLike;
}

/**
 * @description 判断指定槽位在“曲面 + 平面”混合模式下是否为平面槽位。
 * @param slot 槽位键值。
 * @returns 命中平面槽位时返回 `true`。
 */
function isSurfacePlaneMixedPlaneSlot(slot: PlaneSlot): boolean {
  return isSurfacePlaneMixedPair() && resolveSlotSelectionKind(slot) === PlaneSelectionKind.PlaneLike;
}

/**
 * @description 判断当前是否处于“曲面 + 平面 + 平行”模式。
 * @returns 当前混合模式且总约束为平行时返回 `true`。
 */
function isSurfacePlaneParallelMode(): boolean {
  return isSurfacePlaneMixedPair() && currentConstraint.value === PlaneConstraintType.Parallel;
}

/**
 * @description 判断当前是否处于“曲面 + 平面 + 垂直”模式。
 * @returns 当前混合模式且总约束为垂直时返回 `true`。
 */
function isSurfacePlanePerpendicularMode(): boolean {
  return isSurfacePlaneMixedPair() && currentConstraint.value === PlaneConstraintType.Perpendicular;
}


/**
 * @description 判断当前是否处于“曲面 + 点”混合模式。
 * @returns 仅存在一曲面一点评价且无第三参考时返回 `true`。
 */
function isSurfacePointMixedPair(): boolean {
  const firstKind = resolveSlotSelectionKind('reference-1');
  const secondKind = resolveSlotSelectionKind('reference-2');
  const thirdKind = resolveSlotSelectionKind('reference-3');
  const isSurfacePoint = firstKind === PlaneSelectionKind.SurfaceLike && secondKind === PlaneSelectionKind.PointLike;
  const isPointSurface = firstKind === PlaneSelectionKind.PointLike && secondKind === PlaneSelectionKind.SurfaceLike;
  return (
    (isSurfacePoint || isPointSurface)
    && thirdKind === null
  );
}

/**
 * @description 判断指定槽位在“曲面 + 点”混合模式下是否为曲面槽位。
 * @param slot 槽位键值。
 * @returns 命中曲面槽位时返回 `true`。
 */
function isSurfacePointMixedSurfaceSlot(slot: PlaneSlot): boolean {
  return isSurfacePointMixedPair() && resolveSlotSelectionKind(slot) === PlaneSelectionKind.SurfaceLike;
}

/**
 * @description 判断指定槽位在“曲面 + 点”混合模式下是否为点槽位。
 * @param slot 槽位键值。
 * @returns 命中点槽位时返回 `true`。
 */
function isSurfacePointMixedPointSlot(slot: PlaneSlot): boolean {
  return isSurfacePointMixedPair() && resolveSlotSelectionKind(slot) === PlaneSelectionKind.PointLike;
}

/**
 * @description 解析“曲面 + 点”模式下的槽位约束覆盖值。
 * 规则：
 * 1. 曲面槽位始终显示相切语义；
 * 2. 点槽位在默认相切模式下显示重合语义；
 * 3. 当全局切到 OnSurface/Project 时，点槽位统一显示为投影语义；
 * 4. 该覆盖仅用于 PropertyManager 分参考展示，不改变内核约束结构。
 * @param slot 槽位键值。
 * @returns 槽位覆盖约束；无覆盖时返回 `null`。
 */
function resolveSurfacePointMixedSlotConstraint(slot: PlaneSlot): PlaneConstraintType | null {
  if (!isSurfacePointMixedPair()) {
    return null;
  }

  if (isSurfacePointMixedSurfaceSlot(slot)) {
    return PlaneConstraintType.Tangent;
  }

  if (isSurfacePointMixedPointSlot(slot)) {
    if (currentConstraint.value === PlaneConstraintType.Tangent) {
      return PlaneConstraintType.Coincident;
    }
    if (
      currentConstraint.value === PlaneConstraintType.OnSurface
      || currentConstraint.value === PlaneConstraintType.Project
    ) {
      return PlaneConstraintType.Project;
    }
  }

  return null;
}


/**
 * @description 解析“线面混合模式”下的槽位约束覆盖值。
 * 规则：
 * 1. `线 -> 面`：
 *    - 垂直模式：线=重合，面=垂直；
 *    - 两面夹角模式：线=重合，面=两面夹角。
 * 2. `面 -> 线`：
 *    - 垂直模式：面=垂直，线=重合；
 *    - 两面夹角模式：面=两面夹角，线=重合。
 * 3. 非混合模式返回 `null`。
 * @param slot 槽位键值。
 * @returns 槽位覆盖约束；无覆盖时返回 `null`。
 */
function resolveLinePlaneMixedSlotConstraint(slot: PlaneSlot): PlaneConstraintType | null {
  const isPerpendicularMode = currentConstraint.value === PlaneConstraintType.Perpendicular;
  const isAngleMode = currentConstraint.value === PlaneConstraintType.Angle;
  if (!isPerpendicularMode && !isAngleMode) {
    return null;
  }

  const firstKind = resolveSlotSelectionKind('reference-1');
  const secondKind = resolveSlotSelectionKind('reference-2');
  const isFirstLineSecondPlane = (
    firstKind === PlaneSelectionKind.Linear
    && (secondKind === PlaneSelectionKind.PlaneLike || secondKind === PlaneSelectionKind.SurfaceLike)
  );
  if (isFirstLineSecondPlane) {
    if (slot === 'reference-1') {
      return PlaneConstraintType.Coincident;
    }
    if (slot === 'reference-2') {
      return isPerpendicularMode
        ? PlaneConstraintType.Perpendicular
        : PlaneConstraintType.Angle;
    }
    return null;
  }

  const isFirstPlaneSecondLine = (
    (firstKind === PlaneSelectionKind.PlaneLike || firstKind === PlaneSelectionKind.SurfaceLike)
    && secondKind === PlaneSelectionKind.Linear
  );
  if (isFirstPlaneSecondLine) {
    if (slot === 'reference-1') {
      return isPerpendicularMode
        ? PlaneConstraintType.Perpendicular
        : PlaneConstraintType.Angle;
    }
    if (slot === 'reference-2') {
      return PlaneConstraintType.Coincident;
    }
    return null;
  }

  return null;
}


/**
 * @description 解析“线 + 点”模式下的槽位约束覆盖值。
 * 规则：
 * 1. 当前总约束为法向时，线槽位显示为垂直语义；
 * 2. 点槽位保持重合语义；
 * 3. 该覆盖仅用于 PropertyManager 分槽位展示，不改变内核仍以 `Normal` 解算的事实。
 * @param slot 槽位键值。
 * @returns 槽位覆盖约束；无覆盖时返回 `null`。
 */
function resolveLinePointMixedSlotConstraint(slot: PlaneSlot): PlaneConstraintType | null {
  if (currentConstraint.value !== PlaneConstraintType.Normal) {
    return null;
  }

  const firstKind = resolveSlotSelectionKind('reference-1');
  const secondKind = resolveSlotSelectionKind('reference-2');
  const thirdKind = resolveSlotSelectionKind('reference-3');
  const isLinePoint = (
    firstKind === PlaneSelectionKind.Linear
    && secondKind === PlaneSelectionKind.PointLike
    && thirdKind === null
  );
  if (!isLinePoint) {
    return null;
  }

  if (slot === 'reference-1') {
    return PlaneConstraintType.Perpendicular;
  }
  if (slot === 'reference-2') {
    return PlaneConstraintType.Coincident;
  }
  return null;
}


/**
 * @description 解析“点 + 面 + 面”模式下的槽位约束覆盖值。
 * 规则：
 * 1. 第一参考点保持重合语义；
 * 2. 第二、第三参考面在垂直模式下都显示为垂直；
 * 3. 仅当前约束为垂直且当前选择顺序为 点 -> 面 -> 面 时生效。
 * @param slot 槽位键值。
 * @returns 槽位覆盖约束；无覆盖时返回 `null`。
 */
function resolvePointPlanePlaneSlotConstraint(slot: PlaneSlot): PlaneConstraintType | null {
  if (currentConstraint.value !== PlaneConstraintType.Perpendicular) {
    return null;
  }

  const firstKind = resolveSlotSelectionKind('reference-1');
  const secondKind = resolveSlotSelectionKind('reference-2');
  const thirdKind = resolveSlotSelectionKind('reference-3');
  const isPointPlanePlane = (
    firstKind === PlaneSelectionKind.PointLike
    && (secondKind === PlaneSelectionKind.PlaneLike || secondKind === PlaneSelectionKind.SurfaceLike)
    && (thirdKind === PlaneSelectionKind.PlaneLike || thirdKind === PlaneSelectionKind.SurfaceLike)
  );
  if (!isPointPlanePlane) {
    return null;
  }

  if (slot === 'reference-1') {
    return PlaneConstraintType.Coincident;
  }
  if (slot === 'reference-2' || slot === 'reference-3') {
    return PlaneConstraintType.Perpendicular;
  }
  return null;
}

/**
 * @description 解析“点(+点) + 面”平行模式下的槽位约束覆盖值。
 * 规则：
 * 1. 当前总约束为平行时，点槽位保持重合语义；
 * 2. 面槽位显示平行语义；
 * 3. 兼容 `点 -> 面` 与 `点 -> 点 -> 面` 两种输入顺序收口后的槽位展示。
 * @param slot 槽位键值。
 * @returns 槽位覆盖约束；无覆盖时返回 `null`。
 */
function resolvePointPlaneParallelSlotConstraint(slot: PlaneSlot): PlaneConstraintType | null {
  if (currentConstraint.value !== PlaneConstraintType.Parallel) {
    return null;
  }

  const firstKind = resolveSlotSelectionKind('reference-1');
  const secondKind = resolveSlotSelectionKind('reference-2');
  const thirdKind = resolveSlotSelectionKind('reference-3');
  const isPointPlane = (
    firstKind === PlaneSelectionKind.PointLike
    && (secondKind === PlaneSelectionKind.PlaneLike || secondKind === PlaneSelectionKind.SurfaceLike)
  );
  const isPointPointPlane = (
    firstKind === PlaneSelectionKind.PointLike
    && secondKind === PlaneSelectionKind.PointLike
    && (thirdKind === PlaneSelectionKind.PlaneLike || thirdKind === PlaneSelectionKind.SurfaceLike)
  );
  if (!isPointPlane && !isPointPointPlane) {
    return null;
  }

  const slotKind = resolveSlotSelectionKind(slot);
  if (slotKind === PlaneSelectionKind.PointLike) {
    return PlaneConstraintType.Coincident;
  }
  if (slotKind === PlaneSelectionKind.PlaneLike || slotKind === PlaneSelectionKind.SurfaceLike) {
    return PlaneConstraintType.Parallel;
  }
  return null;
}

/**
 * @description 解析“曲面 + 平面”模式下的槽位约束覆盖值。
 * 规则：
 * 1. 当前总约束切到垂直后，曲面槽位保留相切语义；
 * 2. 平面槽位显示垂直语义；
 * 3. 该模式仅负责 UI 分参考展示，不直接代表已得到唯一解。
 * @param slot 槽位键值。
 * @returns 槽位覆盖约束；无覆盖时返回 `null`。
 */
function resolveSurfacePlaneMixedSlotConstraint(slot: PlaneSlot): PlaneConstraintType | null {
  if (!isSurfacePlaneMixedPair()) {
    return null;
  }

  if (isSurfacePlaneMixedSurfaceSlot(slot)) {
    return PlaneConstraintType.Tangent;
  }

  if (isSurfacePlaneMixedPlaneSlot(slot)) {
    if (
      currentConstraint.value === PlaneConstraintType.Perpendicular
      || currentConstraint.value === PlaneConstraintType.Parallel
    ) {
      return currentConstraint.value;
    }
  }
  return null;
}

/**
 * @description 解析指定槽位应显示的约束值。
 * @param slot 槽位键值。
 * @returns 槽位显示约束值。
 */
function resolveConstraintValueBySlot(slot: PlaneSlot): PlaneConstraintType {
  return resolveSurfacePlaneMixedSlotConstraint(slot)
    ?? resolveSurfacePointMixedSlotConstraint(slot)
    ?? resolveLinePointMixedSlotConstraint(slot)
    ?? resolvePointPlaneParallelSlotConstraint(slot)
    ?? resolvePointPlanePlaneSlotConstraint(slot)
    ?? resolveLinePlaneMixedSlotConstraint(slot)
    ?? currentConstraint.value;
}


/**
 * @description 当前可用约束集合。
 */
const availableConstraints = computed(() => localSnapshot.value.referencePlaneAvailableConstraints ?? [PlaneConstraintType.Auto]);

/**
 * @description 距离/角度共享标量。
 */
const scalarValue = computed(() => localSnapshot.value.referencePlaneDistanceOrAngle ?? 0);

/**
 * @description 角度显示值。
 */
const angleValue = computed(() => (
  currentConstraint.value === PlaneConstraintType.Angle ? scalarValue.value : 90
));

/**
 * @description 距离显示值。
 */
const distanceValue = computed(() => (
  roundNullableToEightDecimalPlaces(
    currentConstraint.value === PlaneConstraintType.Distance ? convertMToMm(scalarValue.value) : 10,
  ) ?? 10
));

/**
 * @description 反转法线开关。
 */
const reverseNormal = computed(() => Boolean(localSnapshot.value.referencePlaneReverseNormal));

/**
 * @description 是否显示反转法线开关。
 */
const showReverseNormalOption = computed(() => !isSingleSurfaceTangentPending());

/**
 * @description 反转等距开关。
 */
const reverseOffset = computed(() => Boolean(localSnapshot.value.referencePlaneReverseOffset));

/**
 * @description 原点设在曲线上开关。
 */
const originOnCurve = computed(() => Boolean(localSnapshot.value.referencePlaneOriginOnCurve));

/**
 * @description 等距平面数量。
 */
const planeCount = computed(() => Math.max(1, localSnapshot.value.referencePlanePlaneCount ?? 1));

/**
 * @description 解索引。
 */
const solutionIndex = computed(() => localSnapshot.value.referencePlaneSolutionIndex ?? 0);

/**
 * @description 解数量。
 */
const solutionCount = computed(() => Math.max(1, localSnapshot.value.referencePlaneSolutionCount ?? 1));

/**
 * @description 投影类型。
 */
const projectionType = computed(() => (
  localSnapshot.value.referencePlaneProjectionType ?? PlaneProjectionType.NearestOnSurface
));

/**
 * @description 是否展示解索引行。
 */
const showSolutionIndexField = computed(() => solutionCount.value > 1 && !isSurfacePlaneParallelMode());

/**
 * @description 距离约束是否可用。
 */
const distanceConstraintAvailable = computed(() => isConstraintEnabled(PlaneConstraintType.Distance));


/**
 * @description 两侧对称约束是否可用。
 */
const midPlaneConstraintAvailable = computed(() => isConstraintEnabled(PlaneConstraintType.MidPlane));

/**
 * @description 是否显示“反转等距”选项。
 * 规则：
 * 1. 两侧对称约束显示该开关；
 * 2. 单曲面待相切阶段仅保留该状态，不参与几何求解；
 * 3. 偏移距离模式由数值正负号直接决定方向，不再额外显示反转开关。
 */
const showReverseOffsetOption = computed(() => (
  currentConstraint.value === PlaneConstraintType.MidPlane
  || isSingleSurfaceTangentPending()
  || isSurfacePlanePerpendicularMode()
));

/**
 * @description “反转等距”选项是否可编辑。
 * 规则：
 * 1. 仅两侧对称约束下跟随 MidPlane 可用性；
 * 2. 偏移距离模式不使用该开关。
 */
const reverseOffsetEditable = computed(() => {
  if (isSingleSurfaceTangentPending()) {
    return true;
  }
  if (isSurfacePlanePerpendicularMode()) {
    return isConstraintEnabled(PlaneConstraintType.Perpendicular);
  }
  if (currentConstraint.value === PlaneConstraintType.MidPlane) {
    return midPlaneConstraintAvailable.value;
  }
  return false;
});



/**
 * @description “基准面数”是否可编辑。
 * 规则：
 * 1. 偏移距离模式沿用距离约束可用性；
 * 2. 两侧对称模式沿用 MidPlane 约束可用性；
 * 3. 两面夹角模式沿用 Angle 约束可用性；
 * 4. 其它模式禁用。
 */
const planeCountEditable = computed(() => {
  if (currentConstraint.value === PlaneConstraintType.Distance) {
    return distanceConstraintAvailable.value;
  }
  if (currentConstraint.value === PlaneConstraintType.MidPlane) {
    return midPlaneConstraintAvailable.value;
  }
  if (currentConstraint.value === PlaneConstraintType.Angle) {
    return isConstraintEnabled(PlaneConstraintType.Angle);
  }
  return false;
});

/**
 * @description 是否显示“基准面数”字段。
 * 规则：
 * 1. 仅距离、两侧对称、两面夹角模式显示；
 * 2. 曲面 + 平面切到平行时，用“反转等距”切换相切侧，不显示基准面数。
 */
const showPlaneCountField = computed(() => (
  currentConstraint.value === PlaneConstraintType.Distance
  || currentConstraint.value === PlaneConstraintType.MidPlane
  || currentConstraint.value === PlaneConstraintType.Angle
));


/**
 * @description 角度字段是否可编辑。
 */
const angleFieldEditable = computed(() => currentConstraint.value === PlaneConstraintType.Angle);

/**
 * @description 距离字段是否可编辑。
 */
const distanceFieldEditable = computed(() => currentConstraint.value === PlaneConstraintType.Distance);

/**
 * @description 是否显示“原点设在曲线上”。
 */
const showOriginOnCurveOption = computed(() => currentConstraint.value === PlaneConstraintType.Normal);

/**
 * @description 是否显示投影类型区。
 */
const showProjectionTypeSection = computed(() => (
  
  // 线参考“投影”不显示曲面投影类型；仅曲面投影场景显示。
  (currentConstraint.value === PlaneConstraintType.OnSurface || currentConstraint.value === PlaneConstraintType.Project)
  && Array.from(selectedBySlot.value.values()).some((item) => item.kind === PlaneSelectionKind.SurfaceLike)
  
));

/**
 * @description 判断约束是否可用。
 * @param constraint 约束类型。
 * @returns 是否可用。
 */
function isConstraintEnabled(constraint: PlaneConstraintType): boolean {
  // 当前约束始终允许点击，避免已选项被错误置灰。
  if (constraint === currentConstraint.value) {
    return true;
  }
  return availableConstraints.value.includes(constraint);
}


/**
 * @description 判断指定槽位的约束是否可用。
 * 规则：
 * 1. 点参考面板固定允许“重合/投影”；
 * 2. 其它面板沿用全局可用约束判断；
 * 3. 当前约束始终允许点击，避免已选项被误置灰。
 * @param slot 槽位键值。
 * @param constraint 约束类型。
 * @returns 是否可用。
 */
function isConstraintEnabledForSlot(slot: PlaneSlot, constraint: PlaneConstraintType): boolean {
  const surfacePlaneMixedConstraint = resolveSurfacePlaneMixedSlotConstraint(slot);
  if (surfacePlaneMixedConstraint === PlaneConstraintType.Tangent) {
    return constraint === PlaneConstraintType.Tangent;
  }
  const surfacePointMixedConstraint = resolveSurfacePointMixedSlotConstraint(slot);
  if (surfacePointMixedConstraint === PlaneConstraintType.Tangent) {
    return constraint === PlaneConstraintType.Tangent;
  }
  if (isSurfacePointMixedPointSlot(slot)) {
    if (constraint === surfacePointMixedConstraint) {
      return true;
    }
    if (constraint === PlaneConstraintType.Project) {
      return isConstraintEnabled(PlaneConstraintType.Project) || isConstraintEnabled(PlaneConstraintType.OnSurface);
    }
    return false;
  }
  if (isSurfacePlaneMixedPlaneSlot(slot)) {
    const canToggleSurfacePlaneConstraint = (
      constraint === PlaneConstraintType.Perpendicular
      || constraint === PlaneConstraintType.Parallel
    );
    return canToggleSurfacePlaneConstraint && isConstraintEnabled(constraint);
  }

  
  /**
   * 在线点混合场景下：
   * 1. 线槽位虽然显示为“垂直”，但底层仍处于 `Normal` 求解；
   * 2. 因此需要把“垂直”保留为当前显示选项，同时继续允许用户切到全局 `Coincident`；
   * 3. 点槽位固定保持“重合”语义，不开放额外切换。
   */
  const linePointMixedConstraint = resolveLinePointMixedSlotConstraint(slot);
  if (linePointMixedConstraint !== null) {
    if (constraint === linePointMixedConstraint) {
      return true;
    }

    const panelType = resolveSlotConstraintPanelType(slot);
    if (panelType === PlaneSlotConstraintPanelType.Line && constraint === PlaneConstraintType.Coincident) {
      return isConstraintEnabled(PlaneConstraintType.Coincident);
    }
    return false;
  }
  

  const mixedConstraint = resolveLinePlaneMixedSlotConstraint(slot);
  if (mixedConstraint !== null) {
    if (constraint === mixedConstraint) {
      return true;
    }
    
    /**
     * 在线面混合场景下：
     * 1. 线槽位固定保持“重合”语义；
     * 2. 面槽位允许在“垂直/两面夹角”之间手动切换。
     */
    const panelType = resolveSlotConstraintPanelType(slot);
    if (panelType === PlaneSlotConstraintPanelType.Plane) {
      const canTogglePlaneConstraint = (
        constraint === PlaneConstraintType.Perpendicular
        || constraint === PlaneConstraintType.Angle
      );
      if (canTogglePlaneConstraint) {
        return isConstraintEnabled(constraint);
      }
    }
    return false;
    
  }

  if (constraint === currentConstraint.value) {
    return true;
  }

  const panelType = resolveSlotConstraintPanelType(slot);
  if (panelType === PlaneSlotConstraintPanelType.Point) {
    return constraint === PlaneConstraintType.Coincident || constraint === PlaneConstraintType.Project;
  }
  return isConstraintEnabled(constraint);
}


/**
 * @description 约束文案映射。
 * @param constraint 约束类型。
 * @returns 中文文案。
 */
function resolveConstraintLabel(constraint: PlaneConstraintType): string {
  const labels: Record<PlaneConstraintType, string> = {
    [PlaneConstraintType.Auto]: '自动',
    [PlaneConstraintType.Coincident]: '重合',
    [PlaneConstraintType.Parallel]: '平行',
    [PlaneConstraintType.Perpendicular]: '垂直',
    [PlaneConstraintType.Normal]: '法向',
    [PlaneConstraintType.Angle]: '两面夹角',
    [PlaneConstraintType.Distance]: '偏移距离',
    [PlaneConstraintType.MidPlane]: '两侧对称',
    [PlaneConstraintType.Tangent]: '相切',
    [PlaneConstraintType.OnSurface]: '在曲面上',
    [PlaneConstraintType.Project]: '投影',
  };
  return labels[constraint] ?? String(constraint);
}

/**
 * @description 切换激活槽位。
 * @param slot 槽位。
 */
function setActiveSlot(slot: PlaneSlot): void {
  getViewer()?.referencePlaneController?.setActiveSlot(slot as unknown as PlaneSlotKey);
  syncReferencePlaneSnapshot();
}

/**
 * @description 设置约束。
 * @param slot 槽位键值。
 * @param constraint 目标约束。
 */
function setConstraint(slot: PlaneSlot, constraint: PlaneConstraintType): void {
  // 禁用态不允许切换，保持与按钮置灰一致。
  if (!isConstraintEnabledForSlot(slot, constraint)) {
    return;
  }

  
  /**
   * 混合模式下，槽位约束仅用于“分参考语义展示”，
   * 不应回写为全局约束，避免覆盖当前垂直求解模式。
   */
  const surfacePlaneMixedConstraint = resolveSurfacePlaneMixedSlotConstraint(slot);
  const mixedConstraint = surfacePlaneMixedConstraint === PlaneConstraintType.Tangent
    ? PlaneConstraintType.Tangent
    : resolveLinePlaneMixedSlotConstraint(slot);
  const previousConstraint = currentConstraint.value;
  if (mixedConstraint !== null && constraint === mixedConstraint) {
    return;
  }

  const surfacePointMixedConstraint = resolveSurfacePointMixedSlotConstraint(slot);
  if (surfacePointMixedConstraint !== null && constraint === surfacePointMixedConstraint) {
    return;
  }

  const linePointMixedConstraint = resolveLinePointMixedSlotConstraint(slot);
  if (linePointMixedConstraint !== null && constraint === linePointMixedConstraint) {
    return;
  }
  

  getViewer()?.referencePlaneController?.setConstraint(constraint);

  
  /**
   * 垂直语义本质是 90°。
   * 在线面混合场景从“垂直”切到“两面夹角”时，
   * 即使当前标量沿用了其它模式（如偏移距离），也要回填 90°，
   * 以保证切换瞬间保持原状态不跳变。
   */
  const shouldInitAngleFromPerpendicular = (
    constraint === PlaneConstraintType.Angle
    && previousConstraint === PlaneConstraintType.Perpendicular
  );
  

  // 切到角度且当前数值为空时回填常用默认值。
  if (constraint === PlaneConstraintType.Angle && (shouldInitAngleFromPerpendicular || Math.abs(scalarValue.value) < 1e-9)) {
    getViewer()?.referencePlaneController?.setDistanceOrAngle(90);
  }

  // 切到距离且当前数值为空时回填常用默认值。
  if (constraint === PlaneConstraintType.Distance && Math.abs(scalarValue.value) < 1e-9) {
    getViewer()?.referencePlaneController?.setDistanceOrAngle(convertMmToM(10));
  }

  syncReferencePlaneSnapshot();
}

/**
 * @description 处理约束单选变化。
 * @param slot 槽位键值。
 * @param value 单选值。
 */
function handleConstraintRadioChange(slot: PlaneSlot, value: PlaneConstraintType | string | null): void {
  if (!value) {
    return;
  }
  setConstraint(slot, value as PlaneConstraintType);
}

/**
 * @description 更新距离/角度数值。
 * @param value 新值。
 */
function setDistanceOrAngle(value: number): void {
  const nextValue = currentConstraint.value === PlaneConstraintType.Distance
    ? convertMmToM(roundNullableToEightDecimalPlaces(value) ?? 0)
    : (roundNullableToEightDecimalPlaces(value) ?? 0);
  getViewer()?.referencePlaneController?.setDistanceOrAngle(nextValue);
  syncReferencePlaneSnapshot();
}

/**
 * @description 设置反转法线。
 * @param value 开关值。
 */
function setReverseNormal(value: boolean): void {
  getViewer()?.referencePlaneController?.setReverseNormal(value);
  syncReferencePlaneSnapshot();
}

/**
 * @description 设置反转等距。
 * @param value 开关值。
 */
function setReverseOffset(value: boolean): void {
  getViewer()?.referencePlaneController?.setReverseOffset(value);
  syncReferencePlaneSnapshot();
}

/**
 * @description 设置“原点设在曲线上”。
 * @param value 开关值。
 */
function setOriginOnCurve(value: boolean): void {
  getViewer()?.referencePlaneController?.setOriginOnCurve(value);
  syncReferencePlaneSnapshot();
}

/**
 * @description 设置多平面数量。
 * @param value 平面数量。
 */
function setPlaneCount(value: number): void {
  getViewer()?.referencePlaneController?.setPlaneCount(value);
  syncReferencePlaneSnapshot();
}

/**
 * @description 设置候选解索引。
 * @param value 索引值。
 */
function setSolutionIndex(value: number): void {
  getViewer()?.referencePlaneController?.setSolutionIndex(value);
  syncReferencePlaneSnapshot();
}

/**
 * @description 设置投影类型。
 * @param value 投影类型。
 */
function setProjectionType(value: PlaneProjectionType): void {
  getViewer()?.referencePlaneController?.setProjectionType(value);
  syncReferencePlaneSnapshot();
}

/**
 * @description 打开右键菜单。
 * @param event 鼠标事件。
 * @param entityId 参考实体 ID。
 */
function openReferenceContextMenu(event: MouseEvent, entityId: string | null): void {
  if (!entityId) {
    return;
  }
  contextMenuEntityId.value = entityId;
  contextMenuX.value = Math.floor(event.clientX);
  contextMenuY.value = Math.floor(event.clientY);
  showContextMenu.value = true;
}

/**
 * @description 处理右键菜单动作。
 * @param key 菜单项键值。
 */
function handleContextMenuSelect(key: string): void {
  showContextMenu.value = false;
  if (key !== 'delete-reference') {
    return;
  }
  if (!contextMenuEntityId.value) {
    return;
  }
  const controller = getViewer()?.referencePlaneController;
  controller?.removeSelection(contextMenuEntityId.value);
  controller?.syncSelectionVisuals();
  syncReferencePlaneSnapshot();
  contextMenuEntityId.value = null;
}


/**
 * @description 判断指定参考槽位是否需要显示“两侧对称”标识。
 * @param slot 参考槽位。
 * @returns 当当前约束为 MidPlane 且槽位为第一/第二参考且已选中时返回 true。
 */
function showSlotMidPlaneTag(slot: PlaneSlot): boolean {
  if (currentConstraint.value !== PlaneConstraintType.MidPlane) {
    return false;
  }
  if (slot !== 'reference-1' && slot !== 'reference-2') {
    return false;
  }
  return selectedBySlot.value.has(slot);
}


</script>

<style scoped>
/*------ 基准面属性面板（第一参考对齐截图）开始------*/
.sw-plane-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.status-banner {
  border: 1px solid var(--n-border-color, color-mix(in srgb, var(--n-text-color, #1f2937) 24%, transparent));
  border-radius: 2px;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 600;
}

.status-success {
  background: #75e26d;
  color: #0f2e0b;
}

.status-warning {
  background: #f9d56e;
  color: #4a3a00;
}

.status-error {
  background: #ff9f9f;
  color: #4a0f0f;
}

.reference-input {
  width: 100%;
  min-height: 30px;
  display: grid;
  grid-template-columns: 6px 1fr;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--n-border-color, color-mix(in srgb, var(--n-text-color, #1f2937) 24%, transparent));
  border-radius: 4px;
  background: transparent;
  padding: 0 8px;
  cursor: pointer;
  text-align: left;
}

.reference-input.active {
  border-color: var(--n-primary-color, var(--puppy-primary));
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 12%, transparent);
}

.slot-accent {
  width: 4px;
  height: 18px;
  border-radius: 1px;
}

.reference-text {
  font-size: 12px;
  color: var(--n-text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.slot-midplane-tag {
  margin-top: 6px;
  margin-left: 2px;
  font-size: 12px;
  line-height: 18px;
  color: var(--n-text-color-2);
}

.reference-detail {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/*------ 基准面样式对齐基准点-偏移距离 开始------*/
.scalar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--n-border-color, color-mix(in srgb, var(--n-text-color, #1f2937) 24%, transparent));
  border-radius: 4px;
  background: transparent;
  padding: 4px 8px;
}

.scalar-name {
  font-size: 12px;
  color: var(--n-text-color);
  white-space: nowrap;
}

.unit-text {
  font-size: 12px;
  color: var(--n-text-color-2);
  min-width: 18px;
  text-align: right;
}
/*------ 基准面样式对齐基准点-偏移距离 结束------*/

.reverse-offset-check {
  margin-left: 2px;
}

.count-row {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  align-items: center;
}

.count-name {
  font-size: 12px;
  color: var(--n-text-color);
  white-space: nowrap;
}

.projection-block {
  margin-top: 8px;
  border-top: 1px solid var(--n-divider-color);
  padding-top: 8px;
}

.projection-title {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  color: var(--n-text-color);
}

:deep(.n-radio .n-radio__label) {
  color: var(--n-text-color);
}

:deep(.n-checkbox .n-checkbox__label) {
  color: var(--n-text-color);
}

:deep(.n-input-number .n-input__input-el) {
  color: var(--n-text-color);
}
/*------ 基准面属性面板（第一参考对齐截图）结束------*/
/*------ 基准面偏移距离输入样式对齐基准点 开始------*/
/**
 * 基准面偏移距离输入行样式覆盖：
 * 1. 去除行级背景与边框外框，保持与基准点 X 输入一致的纯输入框视觉。
 * 2. 隐藏偏移距离单位文本（mm），避免在输入框后显示单位标签。
 * 3. 统一输入控件使用 Naive UI 主题变量，保持亮色与深色面板都可读。
 */
.scalar-row {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
}

/**
 * 偏移距离右侧单位文本隐藏：
 * - 用户要求不显示 mm 后缀；
 * - 保留 DOM 结构，使用样式级隐藏保证最小改动。
 */
.scalar-row .unit-text {
  display: none !important;
}

/**
 * 偏移距离输入控件外观对齐基准点 X 输入框：
 * - 去除输入容器额外背景和描边框；
 * - 仅保留输入框本身样式。
 */
.scalar-row .scalar-editor {
  width: 100%;
}

.scalar-row :deep(.n-input-number) {
  width: 100%;
  background: transparent !important;
}

.scalar-row :deep(.n-input-number .n-input-wrapper) {
  background: transparent !important;
  border: 1px solid var(--n-border-color, color-mix(in srgb, var(--n-text-color, #1f2937) 24%, transparent)) !important;
  box-shadow: none !important;
}

.scalar-row :deep(.n-input-number .n-input__input-el) {
  color: var(--n-text-color) !important;
}

/**
 * 角度输入项隐藏：
 * 1. 用户要求移除基准面中的角度输入控件；
 * 2. 仅隐藏 UI 行，不改动求解器与数据模型，避免引入链路回归；
 * 3. 兼容不同渲染结构（类名/数据属性/首行兜底）选择器。
 */
.scalar-row.scalar-row--angle,
.scalar-row[data-scalar-kind='angle'],
.scalar-row[data-scalar-type='angle'] {
  display: none !important;
}

.first-reference-slot .scalar-row:first-of-type {
  display: none !important;
}
/*------ 基准面偏移距离输入样式对齐基准点 结束------*/
</style>
